/**
 * The look of a switch, with none of the behaviour.
 *
 * React Native's Switch renders a real `<input type="checkbox">` on web. That
 * input is 40x20, focusable, and carries no accessible name — so on a row that
 * is itself the control (see the notification preferences), a screen reader
 * finds the labelled row AND a bare unlabelled checkbox inside it, and a
 * pointer finds a 20pt target inside a 44pt one. `aria-hidden`,
 * `accessibilityElementsHidden` and `pointerEvents` were all tried; RNW does
 * not forward them to the input.
 *
 * So the row owns the semantics — role, state, label, press — and this owns
 * only the pixels. It renders Views, which produce nothing focusable.
 *
 * The dimensions match the platform switch closely enough that nothing else on
 * those screens had to move.
 */
import { View } from 'react-native'
import Animated, { useAnimatedStyle, useReducedMotion, withTiming } from 'react-native-reanimated'
import { COLORS } from '@/lib/brand'
import { useSemanticColors, DURATION } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'

const TRACK_WIDTH = 44
const TRACK_HEIGHT = 26
const THUMB = 22
const INSET = (TRACK_HEIGHT - THUMB) / 2

type ToggleTrackProps = {
  value: boolean
  disabled?: boolean
  isDark: boolean
}

export function ToggleTrack({ value, disabled = false, isDark }: ToggleTrackProps) {
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const duration = reducedMotion ? 0 : DURATION.fast

  const trackStyle = useAnimatedStyle(() => ({
    backgroundColor: withTiming(value ? COLORS.coral.dark : colors.border, { duration }),
  }))
  const thumbStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: withTiming(value ? TRACK_WIDTH - THUMB - INSET : INSET, { duration }) },
    ],
  }))

  return (
    <Animated.View
      // Not accessible, not focusable: the row around it is the control.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        {
          width: TRACK_WIDTH,
          height: TRACK_HEIGHT,
          borderRadius: TRACK_HEIGHT / 2,
          justifyContent: 'center',
          opacity: disabled ? 0.5 : 1,
        },
        trackStyle,
      ]}
    >
      <Animated.View
        style={[
          {
            width: THUMB,
            height: THUMB,
            borderRadius: THUMB / 2,
            backgroundColor: '#FFFFFF',
            // The knob is white on both track colours, so on the OFF track it
            // is the only thing telling you which side it sits on. Without a
            // shadow the light OFF switch was a flat grey smudge — the same
            // lift every platform switch gives its knob is what makes the
            // state readable at a glance.
            ...WEB_SHADOWS.sm,
          },
          thumbStyle,
        ]}
      >
        <View />
      </Animated.View>
    </Animated.View>
  )
}
